import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Lock, Eye, EyeOff, ArrowLeft, Loader2, CheckCircle2, AlertCircle, ShieldCheck, MailWarning } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const REQUIREMENTS = [
  { label: "Al menos 12 caracteres", test: (p) => p.length >= 12 },
  { label: "Una letra mayúscula (A-Z)", test: (p) => /[A-Z]/.test(p) },
  { label: "Una letra minúscula (a-z)", test: (p) => /[a-z]/.test(p) },
  { label: "Un número (0-9)", test: (p) => /\d/.test(p) },
  { label: "Un símbolo (!@#$%^&*...)", test: (p) => /[!@#$%^&*()_+\-=[\]{}|;:,.<>?/]/.test(p) },
];

const INVALID_MESSAGES = {
  used: (changedAt) => {
    const d = new Date(changedAt);
    const fmt = `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    return `Este enlace ya se usó: tu contraseña fue cambiada el ${fmt}.`;
  },
  replaced: () => "Hay un enlace más reciente. Usa el último correo recibido.",
  expired: () => "El enlace expiró.",
};

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [validating, setValidating] = useState(true);
  const [invalidReason, setInvalidReason] = useState(null);
  const [invalidMsg, setInvalidMsg] = useState("");

  useEffect(() => {
    localStorage.removeItem("afc_token");
    localStorage.removeItem("afc_refresh");
    localStorage.removeItem("afc_user");

    if (!token) {
      setValidating(false);
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/v1/auth/reset-password/validate", { token });
        if (!data.valid) {
          setInvalidReason(data.reason);
          const fn = INVALID_MESSAGES[data.reason] || (() => "Enlace invalido.");
          setInvalidMsg(fn(data.changed_at));
        }
      } catch {
        setInvalidReason("expired");
        setInvalidMsg(INVALID_MESSAGES.expired());
      } finally {
        setValidating(false);
      }
    })();
  }, [token]);

  const allRequirementsMet = REQUIREMENTS.every((r) => r.test(password));
  const passwordsMatch = password && password === confirm;
  const canSubmit = token && allRequirementsMet && passwordsMatch && !loading;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    try {
      await api.post("/v1/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo restablecer la contraseña");
      setLoading(false);
    }
  };

  const InvalidView = () => (
    <div className="p-6 text-center space-y-4" data-testid="reset-token-invalid">
      <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
        <MailWarning className="text-amber-600" size={28} />
      </div>
      <h2 className="text-lg font-bold text-gray-900">Enlace no válido</h2>
      <p className="text-sm text-gray-600">{invalidMsg}</p>
      <button
        onClick={() => navigate("/forgot-password")}
        className="px-4 py-2 bg-[#0A3747] hover:bg-[#135166] text-white text-sm rounded transition"
        data-testid="request-new-link"
      >
        Solicitar un enlace nuevo
      </button>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4" data-testid="reset-password-page">
      <div className="w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden">
        <div className="bg-[#0A3747] px-6 py-5 border-b-4 border-[#E0E621]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#E0E621]/20 flex items-center justify-center">
              <ShieldCheck className="text-[#E0E621]" size={20} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#E0E621] font-mono">Alfa-Field Connect</div>
              <h1 className="text-lg font-bold text-white" style={{ fontFamily: "Space Grotesk" }}>Nueva contraseña</h1>
            </div>
          </div>
        </div>

        {validating ? (
          <div className="p-6 flex items-center justify-center gap-2 text-gray-500 text-sm">
            <Loader2 size={16} className="animate-spin" /> Verificando enlace...
          </div>
        ) : done ? (
          <div className="p-6 text-center space-y-4" data-testid="reset-password-success">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
              <CheckCircle2 className="text-emerald-600" size={28} />
            </div>
            <h2 className="text-lg font-bold text-gray-900">¡Contraseña actualizada!</h2>
            <p className="text-sm text-gray-600">Ya puedes iniciar sesión con tu nueva contraseña.</p>
            <p className="text-xs text-gray-400 italic">Redirigiendo al login...</p>
          </div>
        ) : !token ? (
          <div className="p-6 text-center space-y-4">
            <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto">
              <AlertCircle className="text-red-600" size={28} />
            </div>
            <h2 className="text-lg font-bold text-gray-900">Enlace inválido</h2>
            <p className="text-sm text-gray-600">El enlace no contiene un token válido. Solicita uno nuevo desde el login.</p>
            <button
              onClick={() => navigate("/forgot-password")}
              className="px-4 py-2 bg-[#0A3747] hover:bg-[#135166] text-white text-sm rounded transition"
            >
              Solicitar nuevo enlace
            </button>
          </div>
        ) : invalidReason ? (
          <InvalidView />
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <p className="text-sm text-gray-600">
              Define una contraseña nueva que cumpla con la política de seguridad corporativa.
            </p>

            <div>
              <label className="block text-[11px] font-medium text-gray-700 mb-1.5 uppercase tracking-wider font-mono">
                Nueva contraseña
              </label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#E0E621]"
                  placeholder="Mínimo 12 caracteres"
                  required
                  autoFocus
                  data-testid="reset-new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#0A3747]"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-medium text-gray-700 mb-1.5 uppercase tracking-wider font-mono">
                Confirmar contraseña
              </label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className={`w-full pl-10 pr-3 py-2.5 bg-white border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#E0E621] ${confirm && !passwordsMatch ? "border-red-400" : "border-gray-300"}`}
                  placeholder="Repite la contraseña"
                  required
                  data-testid="reset-confirm-password"
                />
              </div>
              {confirm && !passwordsMatch && (
                <p className="text-xs text-red-600 mt-1">Las contraseñas no coinciden.</p>
              )}
            </div>

            {password && (
              <div className="bg-gray-50 border border-gray-200 rounded p-3 space-y-1" data-testid="password-requirements">
                <div className="text-[10px] uppercase font-mono text-gray-500 mb-1.5 tracking-wider">Política de seguridad</div>
                {REQUIREMENTS.map((r, i) => {
                  const ok = r.test(password);
                  return (
                    <div key={i} className={`text-xs flex items-center gap-2 ${ok ? "text-emerald-700" : "text-gray-500"}`}>
                      {ok ? <CheckCircle2 size={12} /> : <span className="w-3 h-3 rounded-full border border-gray-300"></span>}
                      {r.label}
                    </div>
                  );
                })}
              </div>
            )}

            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full px-4 py-2.5 bg-[#0A3747] hover:bg-[#135166] text-white text-sm font-semibold rounded transition flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="submit-reset-password"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : "Restablecer contraseña"}
            </button>

            <button
              type="button"
              onClick={() => navigate("/login")}
              className="w-full flex items-center justify-center gap-1.5 text-xs text-gray-600 hover:text-[#0A3747] transition py-1"
            >
              <ArrowLeft size={12} /> Volver al inicio de sesión
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
